import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useMemo, useEffect } from 'react';
import { track } from '@/lib/analytics';
import { FiArrowLeft } from 'react-icons/fi';
import { Card, Button } from '@/components';
import { apiJson, ApiError, extractApiErrorMessage } from '@/lib/api';
import { useHouseholds } from '@/lib/household-context';
import { formatDateLocal } from '@/lib/utils';
import type {
  UserResponse,
  HouseholdResponse,
  CustodySchedule,
  CalendarResponse,
} from '@/lib/types';
import { SCHEDULE_TYPE_NAMES } from '@/lib/types';

function AddExceptionPage() {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const search = Route.useSearch();

  // Track screen view
  useEffect(() => {
    track('Add Custody Exception Screen');
  }, []);

  // Initialize date from URL param or today
  const initialDate = search.date ?? formatDateLocal(new Date());

  const [selectedSchedule, setSelectedSchedule] = useState<number | null>(null);
  const [exceptionDate, setExceptionDate] = useState(initialDate);
  const [requestType, setRequestType] = useState<'SWAP' | 'TIME_CHANGE' | 'PARTIAL'>('SWAP');
  const [selectedAssignedParent, setSelectedAssignedParent] = useState<number | null>(null);
  const [reason, setReason] = useState('');
  const [pickupTime, setPickupTime] = useState<string>('');
  const [dropoffTime, setDropoffTime] = useState<string>('');
  const [timeWindows, setTimeWindows] = useState<{ start: string; end: string }[]>([{ start: '', end: '' }]);
  const [blockOverrides, setBlockOverrides] = useState<Record<number, { start: string; end: string }>>({});

  // Fetch user
  const userQuery = useQuery({
    queryKey: ['user'],
    queryFn: () => apiJson<UserResponse>(getToken, '/v1/user'),
  });

  // Fetch households
  const householdsQuery = useQuery({
    queryKey: ['households'],
    queryFn: async () => {
      try {
        return await apiJson<HouseholdResponse[]>(getToken, '/v1/households');
      } catch (err) {
        if (err instanceof ApiError && (err.status === 403 || err.status === 404)) {
          return [] as HouseholdResponse[];
        }
        throw err;
      }
    },
  });

  // Fetch schedules
  const schedulesQuery = useQuery({
    queryKey: ['custody-schedules'],
    queryFn: async () => {
      try {
        return await apiJson<CustodySchedule[]>(getToken, '/v1/schedules');
      } catch (err) {
        if (err instanceof ApiError && (err.status === 404 || err.status === 400)) {
          return [] as CustodySchedule[];
        }
        throw err;
      }
    },
  });

  const currentUser = userQuery.data;
  const { currentHouseholdId } = useHouseholds();
  const households = householdsQuery.data ?? [];
  const firstHousehold =
    households.find((h) => h.id === currentHouseholdId) ?? households[0];
  const schedules = useMemo(() => schedulesQuery.data ?? [], [schedulesQuery.data]);
  const parents = firstHousehold?.members ?? [];

  // Get beneficiary info for each schedule
  const schedulesWithBeneficiaries = useMemo(() => {
    return schedules.map((schedule) => {
      const beneficiary = firstHousehold?.beneficiaries.find(
        (b) => b.beneficiary_id === schedule.beneficiary_id
      );
      return {
        ...schedule,
        beneficiaryName: beneficiary?.name ?? 'Unknown',
      };
    });
  }, [schedules, firstHousehold]);

  // Get who normally has custody on the selected date
  const selectedScheduleData = schedulesWithBeneficiaries.find((s) => s.id === selectedSchedule);
  const beneficiaryId = selectedScheduleData?.beneficiary_id;

  const normalCustodyQuery = useQuery({
    queryKey: ['calendar', exceptionDate, exceptionDate, selectedSchedule, beneficiaryId],
    queryFn: async () => {
      if (!beneficiaryId) return null;
      try {
        return await apiJson<CalendarResponse>(
          getToken,
          `/v1/calendar?start_date=${exceptionDate}&end_date=${exceptionDate}&beneficiary_id=${beneficiaryId}`
        );
      } catch (err) {
        if (err instanceof ApiError && (err.status === 404 || err.status === 400)) {
          return null;
        }
        throw err;
      }
    },
    enabled: !!selectedSchedule && !!beneficiaryId,
  });

  const calendarDay = normalCustodyQuery.data?.calendar?.[0];
  const normalAssignment = calendarDay?.assignments.find(
    (a) => a.beneficiary_id === beneficiaryId && !a.is_exception
  );
  const normalCustodyUserId = normalAssignment?.assigned_user_id;
  const normalCustodyParent = normalCustodyUserId
    ? parents.find((p) => p.user_id === normalCustodyUserId)
    : null;
  const transitionType = normalAssignment?.transition_type ?? null;
  const usesTransitionTimes = !!(
    selectedScheduleData?.transition_times &&
    selectedScheduleData.transition_times.length > 0
  );
  const isTimeBlocksSchedule = selectedScheduleData?.schedule_type === 'TIME_BLOCKS';
  const activeBlocksForDate = useMemo(() => {
    if (!isTimeBlocksSchedule || !selectedScheduleData?.time_blocks) return [];
    const target = new Date(`${exceptionDate}T12:00:00`);
    const start = new Date(`${selectedScheduleData.start_date}T12:00:00`);
    const dow = (target.getDay() + 6) % 7; // JS 0=Sunday -> model 0=Monday
    const days = Math.floor((target.getTime() - start.getTime()) / 86400000);
    const weekParity = Math.floor(days / 7) % 2;
    return selectedScheduleData.time_blocks.filter((tb) => {
      if (
        tb.recurrence_type === 'BIWEEKLY' &&
        tb.recurrence_week !== null &&
        tb.recurrence_week !== weekParity
      ) {
        return false;
      }
      return tb.day_of_week === dow;
    });
  }, [isTimeBlocksSchedule, selectedScheduleData, exceptionDate]);

  const getParentName = (userId: number): string => {
    if (userId === currentUser?.id) return 'You';
    const member = parents.find((m) => m.user_id === userId);
    if (!member) return `Parent ${userId}`;
    if (member.first_name) return member.first_name;
    return `Parent ${userId}`;
  };

  // Create exception mutation
  const createExceptionMutation = useMutation({
    mutationFn: async (vars: {
      assignedUserId: number;
      exceptionType: 'SWAP' | 'TIME_CHANGE' | 'PARTIAL';
      pickupTime: string;
      dropoffTime: string;
      timeWindows?: { start: string; end: string }[];
      blockTimeOverrides?: { block_id: number; start_time: string | null; end_time: string | null }[];
    }) => {
      const body: Record<string, unknown> = {
        exception_date: exceptionDate,
        assigned_user_id: vars.assignedUserId,
        exception_type: vars.exceptionType,
        reason: reason.trim() || null,
      };

      // Add times if set (format HH:MM to HH:MM:SS for backend)
      if (vars.pickupTime) {
        body.pickup_time = `${vars.pickupTime}:00`;
      }
      if (vars.dropoffTime) {
        body.dropoff_time = `${vars.dropoffTime}:00`;
      }
      if (vars.timeWindows) {
        body.time_windows = vars.timeWindows.map((w) => ({
          start_time: `${w.start}:00`,
          end_time: `${w.end}:00`,
        }));
      }
      if (vars.blockTimeOverrides) {
        body.block_time_overrides = vars.blockTimeOverrides;
      }

      return apiJson(getToken, `/v1/schedules/${selectedSchedule}/exceptions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['custody-exceptions'] });
      await queryClient.invalidateQueries({ queryKey: ['calendar'] });
      navigate({ to: '/app/calendar' });
    },
    onError: (err: unknown) => {
      alert(extractApiErrorMessage(err, 'Could not create exception.'));
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedSchedule) {
      alert('Please select a custody schedule.');
      return;
    }

    if (requestType === 'PARTIAL') {
      const assigned = selectedAssignedParent ?? currentUser?.id;
      if (!assigned) {
        alert('Select who the visitation time is for.');
        return;
      }
      const windows = timeWindows.filter((w) => w.start && w.end);
      if (windows.length === 0) {
        alert('Add at least one visitation time window with a start and end time.');
        return;
      }
      if (windows.length !== timeWindows.length) {
        alert('Every window needs both a start and an end time. Remove incomplete windows.');
        return;
      }
      createExceptionMutation.mutate({
        assignedUserId: assigned,
        exceptionType: 'PARTIAL',
        pickupTime: '',
        dropoffTime: '',
        timeWindows: windows,
      });
      return;
    }

    if (requestType === 'TIME_CHANGE') {
      if (!normalCustodyUserId) {
        alert('We could not tell who has custody on this date. Pick a covered date.');
        return;
      }

      if (isTimeBlocksSchedule) {
        const overrides = Object.entries(blockOverrides)
          .map(([blockId, times]) => ({
            block_id: Number(blockId),
            start_time: times.start ? `${times.start}:00` : null,
            end_time: times.end ? `${times.end}:00` : null,
          }))
          .filter((o) => o.start_time !== null || o.end_time !== null);
        if (overrides.length === 0) {
          alert('Pick a new start or end time for at least one time block.');
          return;
        }
        // Keep custody with the parent who owns the block being changed —
        // derive from the first overridden block, not the stale parent picker.
        const firstBlock = activeBlocksForDate.find(
          (b) => b.id === overrides[0].block_id
        );
        createExceptionMutation.mutate({
          assignedUserId: firstBlock?.assigned_user_id ?? currentUser!.id,
          exceptionType: 'TIME_CHANGE',
          pickupTime: '',
          dropoffTime: '',
          blockTimeOverrides: overrides,
        });
        return;
      }

      let effectivePickup = pickupTime;
      let effectiveDropoff = dropoffTime;
      if (usesTransitionTimes) {
        if (!transitionType) {
          alert('This date has no scheduled handoff. Time changes apply to handoff days.');
          return;
        }
        if (transitionType === 'pickup') {
          effectiveDropoff = '';
          if (!effectivePickup) {
            alert('Enter the new pickup time.');
            return;
          }
        } else {
          effectivePickup = '';
          if (!effectiveDropoff) {
            alert('Enter the new dropoff time.');
            return;
          }
        }
      } else if (!pickupTime && !dropoffTime) {
        alert('Add a pickup or dropoff time.');
        return;
      }

      createExceptionMutation.mutate({
        assignedUserId: normalCustodyUserId,
        exceptionType: 'TIME_CHANGE',
        pickupTime: effectivePickup,
        dropoffTime: effectiveDropoff,
      });
      return;
    }

    if (!selectedAssignedParent) {
      alert('Please select who should have custody on this date.');
      return;
    }
    if (selectedAssignedParent === normalCustodyUserId) {
      alert('Select the other parent for a custody swap, or switch to "Change times".');
      return;
    }

    createExceptionMutation.mutate({
      assignedUserId: selectedAssignedParent,
      exceptionType: 'SWAP',
      pickupTime,
      dropoffTime,
    });
  };

  const isLoading = householdsQuery.isLoading || schedulesQuery.isLoading || userQuery.isLoading;

  if (isLoading) {
    return (
      <div className='min-h-screen bg-sky-light flex items-center justify-center'>
        <div className='animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full' />
      </div>
    );
  }

  if (schedulesWithBeneficiaries.length === 0) {
    return (
      <div className='min-h-screen bg-sky-light'>
        <header className='bg-sky-light border-b border-gray-200 sticky top-0 z-10'>
          <div className='max-w-lg lg:max-w-2xl mx-auto px-6 lg:px-8 py-4'>
            <Link to='/app/calendar' className='inline-flex items-center gap-2 text-primary hover:underline'>
              <FiArrowLeft /> Back to Calendar
            </Link>
          </div>
        </header>
        <div className='max-w-lg lg:max-w-2xl mx-auto px-6 lg:px-8 py-8'>
          <Card className='text-center py-8'>
            <h3
              className='font-bold text-gray-900 mb-2'
              style={{
                fontFamily: "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
              }}
            >
              No Custody Schedules
            </h3>
            <p className='text-gray-500 mb-4'>
              Please create a custody schedule first before adding exceptions.
            </p>
            <Link to='/app/calendar/add-schedule'>
              <Button>Add Custody Schedule</Button>
            </Link>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-sky-light'>
      {/* Header */}
      <header className='bg-sky-light border-b border-gray-200 sticky top-0 z-10'>
        <div className='max-w-lg lg:max-w-2xl mx-auto px-6 lg:px-8 py-4'>
          <Link to='/app/calendar' className='inline-flex items-center gap-2 text-primary hover:underline'>
            <FiArrowLeft /> Back to Calendar
          </Link>
        </div>
      </header>

      <div className='max-w-lg lg:max-w-2xl mx-auto px-6 lg:px-8 py-6'>
        <h1
          className='text-2xl font-bold text-gray-900 mb-6'
          style={{
            fontFamily: "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          }}
        >
          Add Exception
        </h1>

        <form onSubmit={handleSubmit} className='space-y-6'>
          {/* Select Schedule */}
          <div>
            <h2
              className='text-lg font-bold text-gray-900 mb-3'
              style={{
                fontFamily: "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
              }}
            >
              Select Schedule
            </h2>
            <div className='space-y-2'>
              {schedulesWithBeneficiaries.map((schedule) => (
                <button
                  key={schedule.id}
                  type='button'
                  onClick={() => setSelectedSchedule(schedule.id)}
                  className={`w-full text-left p-4 rounded-xl border transition-colors ${
                    selectedSchedule === schedule.id
                      ? 'bg-primary text-white border-primary'
                      : 'bg-white text-gray-900 border-gray-200 hover:border-primary'
                  }`}
                >
                  <p className='font-bold'>{schedule.beneficiaryName}</p>
                  <p
                    className={`text-sm mt-1 ${
                      selectedSchedule === schedule.id ? 'text-white/80' : 'text-gray-500'
                    }`}
                  >
                    {SCHEDULE_TYPE_NAMES[schedule.schedule_type] ?? schedule.schedule_type}
                  </p>
                </button>
              ))}
            </div>
          </div>

          {/* Exception Date */}
          <div>
            <h2
              className='text-lg font-bold text-gray-900 mb-2'
              style={{
                fontFamily: "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
              }}
            >
              Exception Date
            </h2>
            <p className='text-sm text-gray-500 mb-3'>Which date should this exception apply to?</p>
            <input
              type='date'
              value={exceptionDate}
              onChange={(e) => setExceptionDate(e.target.value)}
              className='w-full px-4 py-3 rounded-xl bg-white border border-gray-200 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all'
            />
            {selectedSchedule && normalCustodyParent && (
              <p className='text-sm text-gray-500 mt-2 italic'>
                Normally: {getParentName(normalCustodyParent.user_id)} has custody on this date
              </p>
            )}
          </div>

          {/* Request Type */}
          <div>
            <h2
              className='text-lg font-bold text-gray-900 mb-3'
              style={{
                fontFamily: "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
              }}
            >
              What do you want to change?
            </h2>
            <div className='grid grid-cols-3 gap-1 bg-gray-100 rounded-xl p-1'>
              <button
                type='button'
                onClick={() => setRequestType('TIME_CHANGE')}
                className={`py-3 rounded-lg font-semibold transition-colors ${
                  requestType === 'TIME_CHANGE'
                    ? 'bg-primary text-white'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Change times
              </button>
              <button
                type='button'
                onClick={() => setRequestType('SWAP')}
                className={`py-3 rounded-lg font-semibold transition-colors ${
                  requestType === 'SWAP'
                    ? 'bg-primary text-white'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Swap custody
              </button>
              <button
                type='button'
                onClick={() => {
                  setRequestType('PARTIAL');
                  if (selectedAssignedParent === null && currentUser) {
                    setSelectedAssignedParent(currentUser.id);
                  }
                }}
                className={`py-3 rounded-lg font-semibold transition-colors ${
                  requestType === 'PARTIAL'
                    ? 'bg-primary text-white'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Add visitation
              </button>
            </div>
          </div>

          {/* Custody */}
          <div>
            {requestType === 'SWAP' ? (
              <>
                <h2
                  className='text-lg font-bold text-gray-900 mb-2'
                  style={{
                    fontFamily: "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                  }}
                >
                  Who should have custody?
                </h2>
                <p className='text-sm text-gray-500 mb-3'>
                  Select which parent should have custody on this exception date.
                </p>
                <div className='flex flex-wrap gap-2'>
                  {parents.map((parent) => (
                    <button
                      key={parent.user_id}
                      type='button'
                      onClick={() => setSelectedAssignedParent(parent.user_id)}
                      className={`px-4 py-3 rounded-xl border transition-colors font-semibold ${
                        selectedAssignedParent === parent.user_id
                          ? 'bg-primary text-white border-primary'
                          : 'bg-white text-gray-900 border-gray-200 hover:border-primary'
                      }`}
                    >
                      {getParentName(parent.user_id)}
                    </button>
                  ))}
                </div>
              </>
            ) : requestType === 'PARTIAL' ? (
              <>
                <h2
                  className='text-lg font-bold text-gray-900 mb-2'
                  style={{
                    fontFamily: "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                  }}
                >
                  Who is the visitation time for?
                </h2>
                <p className='text-sm text-gray-500 mb-3'>
                  Select which parent gets these hours on this date. The rest of the day keeps its
                  normal custody.
                </p>
                <div className='flex flex-wrap gap-2'>
                  {parents.map((parent) => (
                    <button
                      key={parent.user_id}
                      type='button'
                      onClick={() => setSelectedAssignedParent(parent.user_id)}
                      className={`px-4 py-3 rounded-xl border transition-colors font-semibold ${
                        selectedAssignedParent === parent.user_id
                          ? 'bg-primary text-white border-primary'
                          : 'bg-white text-gray-900 border-gray-200 hover:border-primary'
                      }`}
                    >
                      {getParentName(parent.user_id)}
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <>
                <h2
                  className='text-lg font-bold text-gray-900 mb-2'
                  style={{
                    fontFamily: "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                  }}
                >
                  Who has custody?
                </h2>
                {normalCustodyParent ? (
                  <p className='text-sm text-gray-500 mb-3'>
                    {getParentName(normalCustodyParent.user_id)} keeps custody — only the handoff
                    time changes.
                  </p>
                ) : (
                  <p className='text-sm text-gray-500 mb-3'>
                    Pick a date covered by the schedule to set a time change.
                  </p>
                )}
                {usesTransitionTimes && !transitionType && (
                  <p className='text-sm text-amber-700'>
                    This date has no scheduled handoff. Choose a handoff day to change its time.
                  </p>
                )}
              </>
            )}
          </div>

          {/* Visitation windows (PARTIAL) */}
          {requestType === 'PARTIAL' && (
            <div>
              <h2
                className='text-lg font-bold text-gray-900 mb-2'
                style={{
                  fontFamily: "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                }}
              >
                Visitation windows
              </h2>
              <p className='text-sm text-gray-500 mb-3'>
                Add up to 4 time windows for this date. Overnight windows (end before start) carry
                into the next day.
              </p>
              <div className='space-y-2'>
                {timeWindows.map((window, idx) => (
                  <div key={idx} className='flex items-center gap-2'>
                    <input
                      type='time'
                      value={window.start}
                      onChange={(e) => {
                        const next = [...timeWindows];
                        next[idx] = { ...next[idx], start: e.target.value };
                        setTimeWindows(next);
                      }}
                      className='flex-1 px-4 py-3 rounded-xl bg-white border border-gray-200 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all'
                    />
                    <span className='text-gray-400 font-semibold'>to</span>
                    <input
                      type='time'
                      value={window.end}
                      onChange={(e) => {
                        const next = [...timeWindows];
                        next[idx] = { ...next[idx], end: e.target.value };
                        setTimeWindows(next);
                      }}
                      className='flex-1 px-4 py-3 rounded-xl bg-white border border-gray-200 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all'
                    />
                    {timeWindows.length > 1 && (
                      <button
                        type='button'
                        onClick={() => setTimeWindows(timeWindows.filter((_, i) => i !== idx))}
                        className='px-3 py-3 rounded-xl border border-gray-200 text-gray-500 hover:text-red-600 hover:border-red-300 transition-colors'
                        aria-label='Remove window'
                      >
                        ✕
                      </button>
                    )}
                  </div>
                ))}
              </div>
              {timeWindows.length < 4 && (
                <button
                  type='button'
                  onClick={() => setTimeWindows([...timeWindows, { start: '', end: '' }])}
                  className='mt-2 px-4 py-2 rounded-xl border border-primary/40 text-primary font-semibold hover:bg-primary/5 transition-colors'
                >
                  + Add another window
                </button>
              )}
            </div>
          )}

          {/* Block time overrides (TIME_CHANGE on TIME_BLOCKS) */}
          {requestType === 'TIME_CHANGE' && isTimeBlocksSchedule && (
            <div>
              <h2
                className='text-lg font-bold text-gray-900 mb-2'
                style={{
                  fontFamily: "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                }}
              >
                New block times
              </h2>
              <p className='text-sm text-gray-500 mb-3'>
                Pick new start/end times for this date's blocks. Leave a field blank to keep the
                current time.
              </p>
              {activeBlocksForDate.length === 0 && (
                <p className='text-sm text-amber-700'>
                  This date has no time blocks from the schedule.
                </p>
              )}
              <div className='space-y-2'>
                {activeBlocksForDate.map((block) => {
                  const current =
                    blockOverrides[block.id] ?? { start: '', end: '' };
                  return (
                    <div
                      key={block.id}
                      className='flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-4 py-2'
                    >
                      <div className='flex-1'>
                        <p className='font-semibold text-gray-900 text-sm'>
                          {getParentName(block.assigned_user_id)}
                          {block.spans_next_day ? ' (overnight)' : ''}
                        </p>
                        <p className='text-xs text-gray-500'>
                          Currently {block.start_time} – {block.end_time}
                        </p>
                      </div>
                      <input
                        type='time'
                        value={current.start}
                        onChange={(e) =>
                          setBlockOverrides({
                            ...blockOverrides,
                            [block.id]: { ...current, start: e.target.value },
                          })
                        }
                        className='w-28 px-3 py-2 rounded-lg bg-white border border-gray-200 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all'
                      />
                      <span className='text-gray-400 text-sm'>to</span>
                      <input
                        type='time'
                        value={current.end}
                        onChange={(e) =>
                          setBlockOverrides({
                            ...blockOverrides,
                            [block.id]: { ...current, end: e.target.value },
                          })
                        }
                        className='w-28 px-3 py-2 rounded-lg bg-white border border-gray-200 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all'
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Reason (Optional) */}
          <div>
            <h2
              className='text-lg font-bold text-gray-900 mb-2'
              style={{
                fontFamily: "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
              }}
            >
              Reason (Optional)
            </h2>
            <p className='text-sm text-gray-500 mb-3'>
              Add a reason for this schedule change request.
            </p>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder='e.g., Special event, vacation, etc.'
              rows={3}
              className='w-full px-4 py-3 rounded-xl bg-white border border-gray-200 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all resize-none'
            />
          </div>

          {/* Pickup Time */}
          {!(requestType === 'TIME_CHANGE' && isTimeBlocksSchedule) &&
            (requestType === 'SWAP' ||
            !usesTransitionTimes ||
            transitionType === 'pickup') && (
            <div>
              <h2
                className='text-lg font-bold text-gray-900 mb-2'
                style={{
                  fontFamily: "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                }}
              >
                {requestType === 'TIME_CHANGE' ? 'New Pickup Time' : 'Pickup Time (Optional)'}
              </h2>
              <p className='text-sm text-gray-500 mb-3'>
                {requestType === 'TIME_CHANGE'
                  ? 'Set the new pickup time for this date.'
                  : 'Override pickup time for this exception date.'}
              </p>
              <div className='flex items-center gap-3'>
                <input
                  type='time'
                  value={pickupTime}
                  onChange={(e) => setPickupTime(e.target.value)}
                  className='flex-1 px-4 py-3 rounded-xl bg-white border border-gray-200 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all'
                />
                {pickupTime && (
                  <button
                    type='button'
                    onClick={() => setPickupTime('')}
                    className='px-3 py-2 text-red-600 hover:text-red-700 font-semibold'
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Dropoff Time */}
          {!(requestType === 'TIME_CHANGE' && isTimeBlocksSchedule) &&
            (requestType === 'SWAP' ||
            !usesTransitionTimes ||
            transitionType === 'dropoff') && (
            <div>
              <h2
                className='text-lg font-bold text-gray-900 mb-2'
                style={{
                  fontFamily: "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                }}
              >
                {requestType === 'TIME_CHANGE' ? 'New Dropoff Time' : 'Dropoff Time (Optional)'}
              </h2>
              <p className='text-sm text-gray-500 mb-3'>
                {requestType === 'TIME_CHANGE'
                  ? 'Set the new dropoff time for this date.'
                  : 'Override dropoff time for this exception date.'}
              </p>
              <div className='flex items-center gap-3'>
                <input
                  type='time'
                  value={dropoffTime}
                  onChange={(e) => setDropoffTime(e.target.value)}
                  className='flex-1 px-4 py-3 rounded-xl bg-white border border-gray-200 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all'
                />
                {dropoffTime && (
                  <button
                    type='button'
                    onClick={() => setDropoffTime('')}
                    className='px-3 py-2 text-red-600 hover:text-red-700 font-semibold'
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Submit */}
          <Button
            type='submit'
            fullWidth
            disabled={
              createExceptionMutation.isPending ||
              !selectedSchedule ||
              (requestType === 'SWAP' ? !selectedAssignedParent : !normalCustodyUserId)
            }
            loading={createExceptionMutation.isPending}
          >
            {requestType === 'TIME_CHANGE'
              ? 'Request Time Change'
              : requestType === 'PARTIAL'
              ? 'Request Visitation'
              : 'Request Swap'}
          </Button>
        </form>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/app/calendar/add-exception')({
  component: AddExceptionPage,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      date: typeof search.date === 'string' ? search.date : undefined,
    };
  },
});
